/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/LaneContextMenu.tsx
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-LANEMENU-001
 * PURPOSE: Render the routed semantic lane/chip menu from registry-built items.
 * ROLE IN ARCHITECTURE: Presentational frame over @quotech/ui PositionedContextMenu.
 * MAIN EXPORTS: LaneContextMenu, LANE_MENU_SEARCH_THRESHOLD, LANE_MENU_VIEWPORT_GUTTER.
 * NON-RESPONSIBILITIES: No routing, no item building, no transports.
 * NOTES FOR FUTURE AI: Mutation items run() FIRST (synchronous optimistic
 *   apply via the Phase 2 runner) and only then close — never close before
 *   the optimistic ack. Geometry (flip/clamp/portal) is owned by the shared
 *   primitive; this file only pins the 16px viewport gutter contract.
 *   change #269 (C3, deferred by decision): the plan called for this menu to
 *   render as a bottom sheet on coarse/touch pointers. That variant does not
 *   exist today — every pointer type gets this SAME role="menu" positioned
 *   dropdown, proven tap-reachable/gutter-clamped/non-overlapping-trigger/
 *   dismissible in e2e/touch/reader-header-touch.spec.ts. Building the sheet
 *   treatment is pending explicit operator input; do not add it speculatively.
 */
import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'

import {
  ContextMenu,
  ContextMenuHeader,
  ContextMenuItem,
} from '../../../_internal/components/ContextMenu'
import { LANE_MENU_HEADINGS } from './menuScopes'
import type { LaneMenuItemModel } from './laneMenuItems'
import type { ReaderOpenMenu } from './useContextMenuRouting'

/** Above this count the menu grows a filter field (plan nested-menu policy). */
export const LANE_MENU_SEARCH_THRESHOLD = 12
/** Exact viewport gutter the lane menu keeps on every edge (plan geometry). */
export const LANE_MENU_VIEWPORT_GUTTER = 16

export interface LaneContextMenuProps {
  menu: ReaderOpenMenu
  items: readonly LaneMenuItemModel[]
  onClose: () => void
}

/**
 * Renders exactly one open lane/chip menu.
 *
 * Inputs: the routed open-menu state, pre-built item models, close callback.
 * Output: a portalled, collision-aware menu; empty item lists render a
 * single disabled "No actions available" row rather than an empty panel.
 * Side effects: item run() callbacks (optimistic mutations, copies, opens).
 */
export function LaneContextMenu({ menu, items, onClose }: LaneContextMenuProps) {
  const [query, setQuery] = useState('')
  const heading = LANE_MENU_HEADINGS[menu.scope.lane]
  const searchable = items.length > LANE_MENU_SEARCH_THRESHOLD

  const visibleItems = useMemo(() => {
    if (!searchable || query.trim() === '') return items
    const needle = query.trim().toLowerCase()
    return items.filter((item) => item.label.toLowerCase().includes(needle))
  }, [items, query, searchable])

  return (
    <ContextMenu
      x={menu.x}
      y={menu.y}
      label={`${heading} actions`}
      restoreFocusTo={menu.restoreFocusTo}
      viewportGutter={LANE_MENU_VIEWPORT_GUTTER}
      onClose={onClose}
    >
      <ContextMenuHeader>{heading}</ContextMenuHeader>
      {searchable && (
        <div className="flex items-center gap-1.5 px-2 pb-1" role="presentation">
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          <input
            type="text"
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter actions…"
            aria-label={`Filter ${heading} actions`}
            className="w-full rounded border border-border bg-transparent px-1.5 py-0.5 text-xs outline-none focus:border-primary"
          />
        </div>
      )}
      {visibleItems.map((item) => (
        <ContextMenuItem
          key={item.id}
          variant={item.danger ? 'danger' : 'default'}
          onClick={() => {
            // Optimistic apply happens synchronously inside run(); closing
            // afterwards satisfies the "close only after optimistic ack"
            // contract without waiting on the server round-trip.
            item.run()
            onClose()
          }}
        >
          {item.label}
        </ContextMenuItem>
      ))}
      {visibleItems.length === 0 && (
        <ContextMenuItem disabled>
          {searchable ? 'No matching actions' : 'No actions available'}
        </ContextMenuItem>
      )}
    </ContextMenu>
  )
}
